import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCallQueueStore } from '../../store/callQueueStore';
import type { QueueFilters } from '../../types';

interface SaveAsQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: QueueFilters;
  matchCount: number;
}

export const SaveAsQueueModal = ({ isOpen, onClose, currentFilters, matchCount }: SaveAsQueueModalProps) => {
  const [name, setName] = useState('');
  const { createQueue, isSaving, saveError } = useCallQueueStore();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      const queue = await createQueue({ name: name.trim(), filtersJson: currentFilters });
      setName('');
      onClose();
      navigate(`/call-queues/${queue.id}`);
    } catch {
      // saveError already set by the store
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Save as Call Queue</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This queue will start with <strong>{matchCount}</strong> {matchCount === 1 ? 'person' : 'people'}.
          </p>
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Queue name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CA Football PROSPECTs"
              className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-800"
              autoFocus
            />
          </label>
          {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save & open
          </button>
        </div>
      </div>
    </div>
  );
};
